/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import React, { useState, useEffect } from 'react';
import TestToolIdentification from './TestToolIdentification';
import TestToolUserCredentials from './TestToolUserCredentials';
import TestToolDigestAuth from './TestToolDigestAuth';
import TestToolHobaAuth from './TestToolHobaAuth';
import TestToolMutualAuth from './TestToolMutualAuth';
import TestToolNegotiateAuth from './TestToolNegotiateAuth';
import TestToolVapidAuth from './TestToolVapidAuth';
import TestToolScramShaAuth from './TestToolScramShaAuth';
import TestToolSecurityFlows from './TestToolSecurityFlows';
import TestToolScopes from './TestToolScopes';
import TestToolAccessCode from './TestToolAccessCode';
import TestToolAccessToken from './TestToolAccessToken';
import TestToolRedirectUri from './TestToolRedirectUri';
import TestToolRefreshToken from './TestToolRefreshToken';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionItem, Select, SelectItem, TextInput } from '@carbon/react';
import TestToolOidcDiscovery from './TestToolOidcDiscovery';
import { findFirstAppWithAccess } from 'utils/common';
import classNames from 'classnames';
const TestToolSecurity = ({ config, options, apps, isEnforced, onChange, endpoint: propsEndpoint }) => {
  const [flags, setFlags] = useState({});
  const [scopes, setScopes] = useState({});
  const [endpoint, setEndpoint] = useState({});
  const { t } = useTranslation();
  const [identification, setIdentification] = useState({
    clientID: null,
    clientSecret: '',
    redirectUri: null,
    redirectUris: [],
    externalApiKeys: {}
  });
  const [userCredentials, setUserCredentials] = useState({
    username: null,
    password: null
  });
  const [httpBearerToken, setHttpBearerToken] = useState('');
  const [httpDigestAuth, setHttpDigestAuth] = useState({
    digestUsername: null,
    digestRealm: '',
    digestNonce: '',
    digestOpaque: '',
    digestResponse: ''
  });
  const [httpMutualAuth, setHttpMutualAuth] = useState({
    mutualVersion: '',
    mutualSid: '',
    mutualNonce: '',
    mutualVkc: ''
  });
  const [httpNegotiateAuth, setHttpNegotiateAuth] = useState({
    negotiateGssapiData: ''
  });
  const [httpHobaAuth, setHttpHobaAuth] = useState({
    hobaKeyIdentifier: '',
    hobaChallenge: '',
    hobaNonce: '',
    hobaSignature: ''
  });
  const [httpVapidAuth, setHttpVapidAuth] = useState({
    vapidJSONWebToken: '',
    vapidJSONWebKey: ''
  });
  const [httpScramShaAuth, setHttpScramShaAuth] = useState({
    scramSessionId: '',
    scramEncryptedCreds: ''
  });

  const [removeFields, setRemoveFields] = useState(false);
  const [currentFlow, setCurrentFlow] = useState('login');
  const [authToken, setAuthToken] = useState({
    code: '',
    access_token: ''
  });

  const changeAuthorizationUrl = (evt) => {
    const newFlags = Object.assign({}, flags, { selectedAuthUrl: get(evt, 'target.value') });
    setFlags(newFlags);
    if (onChange && typeof onChange === 'function') {
      onChange({ flags: newFlags });
    }
  };
  const changeTokenUrl = (evt) => {
    const newFlags = Object.assign({}, flags, { selectedTokenUrl: get(evt, 'target.value') });
    setFlags(newFlags);
    if (onChange && typeof onChange === 'function') {
      onChange({ flags: newFlags });
    }
  };
  const changeRefreshUrl = (evt) => {
    const newFlags = Object.assign({}, flags, { selectedRefreshUrl: get(evt, 'target.value') });
    setFlags(newFlags);
    if (onChange && typeof onChange === 'function') {
      onChange({ flags: newFlags });
    }
  };

  useEffect(() => {
    if (onChange && typeof onChange === 'function') {
      onChange({ identification });
    }
  }, [identification, onChange]);

  useEffect(() => {
    if (config) {
      let newFlags = flags;
      if (config.flags) {
        if (!flags || Object.keys(flags).length === 0 || endpoint !== propsEndpoint) {
          newFlags = config.flags;
        }
      }
      if (!currentFlow || (newFlags !== flags && Object.keys(newFlags).length !== 0)) {
        let initialFlow = Object.keys(config.securityFlows)[0];
        setCurrentFlow(initialFlow);
      }
      if (newFlags.oauthAuthUrl) {
        newFlags.selectedAuthUrl = newFlags.oauthAuthUrl;
      }
      if (newFlags.oauthTokenUrl) {
        newFlags.selectedTokenUrl = newFlags.oauthTokenUrl;
      }
      if (newFlags.oauthRefreshUrl) {
        newFlags.selectedRefreshUrl = newFlags.oauthRefreshUrl;
      }
      if (Array.isArray(newFlags.selectedAuthUrl) && newFlags.selectedAuthUrl.length > 0) {
        newFlags.selectedAuthUrl = newFlags.selectedAuthUrl[0];
      }
      if (Array.isArray(newFlags.selectedTokenUrl) && newFlags.selectedTokenUrl.length > 0) {
        newFlags.selectedTokenUrl = newFlags.selectedTokenUrl[0];
      }
      if (Array.isArray(newFlags.selectedRefreshUrl) && newFlags.selectedRefreshUrl.length > 0) {
        newFlags.selectedRefreshUrl = newFlags.selectedRefreshUrl[0];
      }
      if (
        ((newFlags.oauthFlow === 'accessCode' || newFlags.oauthFlow === 'authorizationCode' || newFlags.oauthFlow === 'implicit') && options.optimiseOauthUX) ||
        newFlags.openIdConnectUrl
      ) {
        setRemoveFields(true);
      } else {
        setRemoveFields(false);
      }
      if (Object.keys(newFlags).length !== 0) {
        setFlags(newFlags);
        if (onChange && typeof onChange === 'function') {
          onChange({ flags: newFlags });
        }
      }
    }
    if (endpoint !== propsEndpoint) {
      setEndpoint(propsEndpoint);
    }
    if (!identification.clientID && apps && apps.length > 0 && apps[0].credentials && apps[0].credentials.length > 0) {
      let initialIdentification = {};

      let initialApp = findFirstAppWithAccess(apps);
      initialIdentification.clientID = initialApp ? initialApp.credentials[0]['client_id'] : apps[0].credentials[0]['client_id'];
      initialIdentification.redirectUris = [''];

      let initialRedirectUri = initialApp ? initialApp.redirectUri : apps[0].redirectUri;
      if (typeof initialRedirectUri === 'string') {
        initialIdentification.redirectUris[0] = initialRedirectUri;
      } else if (initialRedirectUri && initialRedirectUri.length > 0) {
        initialIdentification.redirectUris = initialRedirectUri;
      }
      initialIdentification.redirectUri = initialIdentification.redirectUris[0];

      initialIdentification.externalApiKeys = {};

      setIdentification(initialIdentification);
    }

    if (!userCredentials.username && options.authentication) {
      let initialUserCreds = {};

      initialUserCreds.username = options.authentication.username ? options.authentication.username : null;
      initialUserCreds.password = options.authentication.password ? options.authentication.password : null;

      setUserCredentials(initialUserCreds);
      if (onChange && typeof onChange === 'function') {
        onChange({ userCredentials: initialUserCreds });
      }
    }
  }, [
    apps,
    config,
    currentFlow,
    endpoint,
    flags,
    identification.clientID,
    onChange,
    options.authentication,
    options.optimiseOauthUX,
    propsEndpoint,
    userCredentials.username
  ]);

  const changeSecurityFlow = (flow) => {
    let selectedFlags = config.securityFlows[flow].config;

    let newFlags = Object.assign({}, flags, selectedFlags);
    newFlags.selectedAuthUrl = selectedFlags.oauthAuthUrl;
    newFlags.selectedTokenUrl = selectedFlags.oauthTokenUrl;
    newFlags.selectedRefreshUrl = selectedFlags.oauthRefreshUrl;
    if (Array.isArray(newFlags.selectedAuthUrl) && newFlags.selectedAuthUrl.length > 0) {
      newFlags.selectedAuthUrl = newFlags.selectedAuthUrl[0];
    }
    if (Array.isArray(newFlags.selectedTokenUrl) && newFlags.selectedTokenUrl.length > 0) {
      newFlags.selectedTokenUrl = newFlags.selectedTokenUrl[0];
    }
    if (Array.isArray(newFlags.selectedRefreshUrl) && newFlags.selectedRefreshUrl.length > 0) {
      newFlags.selectedRefreshUrl = newFlags.selectedRefreshUrl[0];
    }

    if (
      ((newFlags.oauthFlow === 'accessCode' || newFlags.oauthFlow === 'authorizationCode' || newFlags.oauthFlow === 'implicit') && options.optimiseOauthUX) ||
      newFlags.openIdConnectUrl
    ) {
      setRemoveFields(true);
    } else {
      setRemoveFields(false);
    }
    setFlags(newFlags);
    if (onChange && typeof onChange === 'function') {
      onChange({ flags: newFlags });
    }
    setCurrentFlow(flow);
  };

  const changeIdentificationHandler = (update) => {
    let creds = Object.assign({}, identification, update);
    setIdentification(creds);
    if (onChange && typeof onChange === 'function') {
      onChange({ identification: creds });
    }
  };

  const changeHttpDigestAuthHandler = (update) => {
    let creds = Object.assign({}, httpDigestAuth, update);
    setHttpDigestAuth(creds);
    if (onChange && typeof onChange === 'function') {
      onChange({ httpDigestAuth: creds });
    }
  };

  const changeHttpHobaAuthHandler = (update) => {
    let creds = Object.assign({}, httpHobaAuth, update);
    setHttpHobaAuth(creds);
    if (onChange && typeof onChange === 'function') {
      onChange({ httpHobaAuth: creds });
    }
  };

  const changeHttpMutualAuthHandler = (update) => {
    let creds = Object.assign({}, httpMutualAuth, update);
    setHttpMutualAuth(creds);
    if (onChange && typeof onChange === 'function') {
      onChange({ httpMutualAuth: creds });
    }
  };

  const changeHttpNegotiateAuthHandler = (update) => {
    let creds = Object.assign({}, httpNegotiateAuth, update);
    setHttpNegotiateAuth(creds);
    if (onChange && typeof onChange === 'function') {
      onChange({ httpNegotiateAuth: creds });
    }
  };

  const changeHttpVapidAuthHandler = (update) => {
    let creds = Object.assign({}, httpVapidAuth, update);
    setHttpVapidAuth(creds);
    if (onChange && typeof onChange === 'function') {
      onChange({ httpVapidAuth: creds });
    }
  };
  const changeHttpScramShaAuthHandler = (update) => {
    let creds = Object.assign({}, httpScramShaAuth, update);
    setHttpScramShaAuth(creds);
    if (onChange && typeof onChange === 'function') {
      onChange({ httpScramShaAuth: creds });
    }
  };

  const changeUserCredentialsHandler = (update) => {
    let creds = Object.assign({}, userCredentials, update);
    setUserCredentials(creds);
    if (onChange && typeof onChange === 'function') {
      onChange({ userCredentials: creds });
    }
  };

  const changeSecurityOauthFlow = (oauthFlow) => {
    let selectedFlags = config.securityFlows[currentFlow].config.oauthFlowsConfig[oauthFlow];
    selectedFlags.oauthFlowsConfig = config.securityFlows[currentFlow].config.oauthFlowsConfig;
    setFlags(selectedFlags);
    if (onChange && typeof onChange === 'function') {
      onChange({ flags: selectedFlags });
    }
  };

  const changeScopes = (scopes) => {
    setScopes(scopes);
  };

  const handleAuthToken = (newAuthToken) => {
    let newToken = Object.assign({}, authToken, newAuthToken);
    setAuthToken(newToken);
    if (onChange && typeof onChange === 'function') {
      onChange({ authToken: newToken });
    }
  };

  const handleHttpBearerChange = (event) => {
    setHttpBearerToken(event.target.value);
    if (onChange && typeof onChange === 'function') {
      onChange({ httpBearerToken: event.target.value });
    }
  };

  const shouldDisplay =
    (flags && (flags.requiresIdentificationSection || flags.requiresAuthorizationSection)) ||
    (config && config.securityFlows && Object.keys(config.securityFlows).length > 1);
  const stateToPass = Object.assign(
    {},
    {
      flags,
      scopes,
      identification,
      userCredentials,
      authToken,
      httpBearerToken,
      httpDigestAuth,
      httpMutualAuth,
      httpNegotiateAuth,
      httpHobaAuth,
      httpVapidAuth,
      httpScramShaAuth,
      removeFields,
      currentFlow
    }
  );

  if (shouldDisplay) {
    return (
      <div className="testToolSection">
        <div role="heading" aria-level={3} className="labelText">
          {t('security')}
        </div>
        <div className="testToolSecurity">
          {config && config.securityFlows && Object.keys(config.securityFlows).length > 1 && (
            <div className="securityFlow">
              <TestToolSecurityFlows flows={config.securityFlows} onChange={changeSecurityFlow} currentFlow={currentFlow} />
            </div>
          )}

          {flags.requiresIdentificationSection && (
            <TestToolIdentification
              flags={flags}
              options={options}
              externalApiKeys={identification.externalApiKeys}
              apps={apps}
              onChange={changeIdentificationHandler}
              t={t}
              isEnforced={isEnforced}
              onCopyClientIdCallback={() => navigator.clipboard.writeText(identification.clientID)}
            />
          )}
          {flags.requiresAuthorizationSection && (
            <Accordion className="testToolAuthorization" align={'start'}>
              <AccordionItem className="testToolAuthorizationInput" title={t('authorization')} open={true}>
                {flags.oauthFlowsConfig && Object.keys(flags.oauthFlowsConfig).length > 1 && (
                  <div className="securityFlow">
                    <TestToolSecurityFlows flows={flags.oauthFlowsConfig} onChange={changeSecurityOauthFlow} t={t} />
                  </div>
                )}
                {flags.requiresUserCredentials && <TestToolUserCredentials userCreds={userCredentials} onChange={changeUserCredentialsHandler} t={t} />}
                {flags.requiresDigestAuth && <TestToolDigestAuth digestCreds={httpDigestAuth} onChange={changeHttpDigestAuthHandler} t={t} />}
                {flags.requiresHobaAuth && <TestToolHobaAuth hobaCreds={httpHobaAuth} onChange={changeHttpHobaAuthHandler} t={t} />}
                {flags.requiresMutualAuth && <TestToolMutualAuth mutualCreds={httpMutualAuth} onChange={changeHttpMutualAuthHandler} t={t} />}
                {flags.requiresNegotiateAuth && <TestToolNegotiateAuth negotiateCreds={httpNegotiateAuth} onChange={changeHttpNegotiateAuthHandler} t={t} />}
                {flags.requiresVapidAuth && <TestToolVapidAuth vapidCreds={httpVapidAuth} onChange={changeHttpVapidAuthHandler} t={t} />}
                {flags.requiresScramShaAuth && <TestToolScramShaAuth scramCreds={httpScramShaAuth} onChange={changeHttpScramShaAuthHandler} t={t} />}

                {flags.oauthScopes && flags.oauthScopes.length > 0 && !removeFields && (
                  <TestToolScopes scopes={flags.oauthScopes} onChange={changeScopes} t={t} />
                )}

                {flags.requiresRedirectUri && !removeFields && <TestToolRedirectUri value={identification} onChange={changeIdentificationHandler} />}
                {flags.oauthAuthUrl && flags.oauthAuthUrl.length > 0 && (
                  <div className="url authUrl">
                    <legend htmlFor="authUrl" className="parameterName">
                      {t('explorer_authorization_url')}
                    </legend>
                    {Array.isArray(flags.oauthAuthUrl) && flags.oauthAuthUrl.length > 1 && (
                      <Select
                        hideLabel
                        data-testid="oauthAuthUrl-selector"
                        id="oauthAuthUrl-select"
                        value={flags.selectedAuthUrl}
                        onChange={changeAuthorizationUrl}
                      >
                        {flags.oauthAuthUrl.map((url, index) => {
                          return <SelectItem data-testid="oauthAuthUrl-item" key={index} value={url} text={url} />;
                        })}
                      </Select>
                    )}
                    {(!Array.isArray(flags.oauthAuthUrl) || flags.oauthAuthUrl.length === 1) && <p className="parameterValue">{flags.oauthAuthUrl}</p>}
                  </div>
                )}
                {flags.oauthTokenUrl && (
                  <div className="url tokenUrl">
                    <legend htmlFor="token-url" className="parameterName">
                      {t('explorer_token_url')}
                    </legend>
                    {Array.isArray(flags.oauthTokenUrl) && flags.oauthTokenUrl.length > 1 && (
                      <Select hideLabel data-testid="oauthTokenUrl-selector" id="oauthTokenUrl-select" value={flags.selectedTokenUrl} onChange={changeTokenUrl}>
                        {flags.oauthTokenUrl.map((url, index) => {
                          return <SelectItem data-testid="oauthTokenUrl-item" key={index} value={url} text={url} />;
                        })}
                      </Select>
                    )}
                    {(!Array.isArray(flags.oauthTokenUrl) || flags.oauthTokenUrl.length === 1) && <p className="parameterValue">{flags.oauthTokenUrl}</p>}
                  </div>
                )}
                {flags.oauthRefreshUrl && (
                  <div className="url refreshUrl">
                    <legend htmlFor="refreshurl" className="parameterName">
                      {t('explorer_refresh_url')}
                    </legend>
                    {Array.isArray(flags.oauthRefreshUrl) && flags.oauthRefreshUrl.length > 1 && (
                      <Select
                        hideLabel
                        data-testid="oauthRefreshUrl-selector"
                        id="oauthRefreshUrl-select"
                        value={flags.selectedRefreshUrl}
                        onChange={changeRefreshUrl}
                      >
                        {flags.oauthRefreshUrl.map((url, index) => {
                          return <SelectItem data-testid="oauthRefreshUrl-item" key={index} value={url} text={url} />;
                        })}
                      </Select>
                    )}
                    {(!Array.isArray(flags.oauthRefreshUrl) || flags.oauthRefreshUrl.length === 1) && (
                      <p className="parameterValue">{flags.selectedRefreshUrl}</p>
                    )}
                  </div>
                )}

                {flags.openIdConnectUrl && (
                  <>
                    <TestToolOidcDiscovery config={stateToPass} t={t} />
                    <TestToolAccessToken config={stateToPass} onChange={handleAuthToken} />
                  </>
                )}

                {flags.requiresOauth && (
                  <div className={classNames('testToolOauth', { oauthFieldsRemoved: removeFields })}>
                    {(flags.oauthFlow === 'accessCode' || flags.oauthFlow === 'authorizationCode') && !removeFields && (
                      <TestToolAccessCode config={stateToPass} onChange={handleAuthToken} />
                    )}
                    <TestToolAccessToken config={stateToPass} onChange={handleAuthToken} />
                    {flags.requiresRefreshToken && authToken.refresh_token && <TestToolRefreshToken config={stateToPass} onClick={handleAuthToken} />}
                  </div>
                )}

                {flags.requiresHttpBearerToken && (
                  <div className="httpBearerToken" key="httpBearer">
                    <TextInput id="bearer" name="bearer" type="input" labelText="Bearer" onChange={handleHttpBearerChange} value={httpBearerToken} />
                  </div>
                )}
              </AccordionItem>
            </Accordion>
          )}
        </div>
      </div>
    );
  } else {
    return null;
  }
};
export default TestToolSecurity;
